import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, BookOpen as BookOpenIcon } from 'lucide-react';

/* ── Sent2ment palette ── */
const C = {
  cream: '#F5F1E8',
  paper: '#FBF8F1',
  paperWarm: '#FAF6EC',
  midnight: '#1C1F2A',
  midnightDeep: '#13151D',
  gold: '#C6A96B',
  goldSoft: 'rgba(198, 169, 107, 0.18)',
  text: '#0B1F3A',
  textSec: '#5A5A5A',
  border: 'rgba(198, 169, 107, 0.25)',
};

/* Faint paper grain — same fractal noise we used previously, kept very low so
   it reads as paper texture rather than a pattern. */
const PAPER_TEXTURE =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.04  0 0 0 0 0.12  0 0 0 0 0.22  0 0 0 0.055 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

/* Soft-touch hardback grain for the cover — kept very low alpha so it reads as
   a barely-there suede / coated-paper texture rather than a visible pattern. */
const COVER_TEXTURE =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='280' height='280'><filter id='c'><feTurbulence type='fractalNoise' baseFrequency='1.7' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.025 0'/></filter><rect width='100%' height='100%' filter='url(%23c)'/></svg>\")";

/* Foil-gold gradient — layered antique gold tones (deep → warm highlight →
   mid → deep) so text and frames read as physical foil stamping rather than
   flat colour. Applied via background-clip on text; via background on lines
   and the inner frame. Tones intentionally avoid yellow/brass. */
const FOIL_GOLD_GRADIENT =
  'linear-gradient(135deg, #8E6F36 0%, #B89559 22%, #DCC082 45%, #B89559 65%, #8E6F36 100%)';
const FOIL_GOLD_GRADIENT_SOFT =
  'linear-gradient(135deg, #8E6F36 0%, #B0904F 30%, #C9A96A 50%, #B0904F 70%, #8E6F36 100%)';

/* ── Cover ───────────────────────────────────────────────────── */
const CoverFace = ({ color, onClick, label }) => {
  const isMidnight = color === 'midnight';

  // Calmer, more natural colour layering — single soft top-light shift over a
  // rich solid base, instead of stacked digital gradients. Navy stays warm
  // and deep, cream stays warm and inviting.
  const tone = isMidnight
    ? {
        base: '#1C1F2A',       // brand midnight navy
        topLight: '#23273450', // very faint warm top sheen (low alpha)
        ink: '#E8DEC5',        // warm off-cream — softened from pure cream
        wordmarkOpacity: 1,
      }
    : {
        base: '#F0E9D6',       // warm parchment cream
        topLight: '#FBF6E550',
        ink: '#2E3140',        // softened midnight (not harsh black)
        wordmarkOpacity: 1,
      };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={label}
      aria-label="Open the journal"
      className="relative block group focus:outline-none"
      style={{
        width: 'min(360px, 86vw)',
        aspectRatio: '0.72 / 1',
        borderRadius: '6px 14px 14px 6px',
        // Calmer satin: a single soft top-light, then the base. No multi-stop
        // diagonal gradients — keeps the surface reading as natural matte
        // material rather than a digitally-rendered effect.
        background:
          `linear-gradient(180deg, ${tone.topLight} 0%, transparent 38%), ` +
          tone.base,
        backgroundImage: COVER_TEXTURE,
        backgroundBlendMode: 'soft-light',
        boxShadow:
          // hardback drop shadow — softer, two-stage so it feels like a held object
          '0 30px 60px -22px rgba(11,18,32,0.50), ' +
          '0 14px 28px -14px rgba(11,18,32,0.30), ' +
          // gentle inner vignette near the spine for soft-touch depth
          'inset 16px 0 26px -18px rgba(0,0,0,0.32), ' +
          'inset -8px -8px 20px -16px rgba(0,0,0,0.12), ' +
          'inset 0 1px 0 rgba(255,255,255,0.05), ' +
          // page edges peeking
          '4px 4px 0 -2px ' + (isMidnight ? '#EFE6CC' : '#E8DCB8') + ', ' +
          '7px 7px 0 -3px ' + (isMidnight ? '#DDD0AF' : '#D2C29C'),
        border: `1px solid ${isMidnight ? 'rgba(0,0,0,0.40)' : 'rgba(120,95,40,0.18)'}`,
        cursor: 'pointer',
        transition: 'transform 600ms cubic-bezier(0.22,1,0.36,1), box-shadow 600ms ease',
        transformOrigin: 'left center',
      }}
    >
      {/* Spine band — thin softer stripe along the left edge */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', top: 0, bottom: 0, left: 0, width: '14px',
          background: isMidnight
            ? 'linear-gradient(to right, rgba(0,0,0,0.42), rgba(0,0,0,0.04) 80%, transparent)'
            : 'linear-gradient(to right, rgba(120,95,40,0.24), rgba(120,95,40,0.03) 80%, transparent)',
          borderRadius: '6px 0 0 6px',
        }}
      />

      {/* Cover content — frame removed; relying on breathing space + foil
          stamping for hierarchy. */}
      <div
        className="relative h-full flex flex-col items-center px-10 py-14 text-center"
        style={{ color: tone.ink }}
      >
        {/* Top hairline — foil, very subtle */}
        <div
          aria-hidden="true"
          style={{
            width: '32px', height: '1px',
            background: FOIL_GOLD_GRADIENT_SOFT,
            opacity: 0.7,
            marginBottom: '28px',
            borderRadius: '1px',
          }}
        />

        {/* Brand wordmark — Pinyon Script (the brand's signature font, used in
            gold elsewhere on the site) rendered as foil so it reads as the
            real Sent2ment mark, not a typed label. */}
        <p
          style={{
            fontFamily: "'Pinyon Script', cursive",
            fontSize: 'clamp(28px, 5vw, 36px)',
            lineHeight: 1,
            letterSpacing: '0.005em',
            backgroundImage: FOIL_GOLD_GRADIENT,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
            opacity: tone.wordmarkOpacity,
            filter: isMidnight
              ? 'drop-shadow(0 1px 0 rgba(0,0,0,0.35))'
              : 'drop-shadow(0 1px 0 rgba(120,95,40,0.16))',
            paddingBottom: '4px', // descenders breathing room
          }}
        >
          Sent2ment
        </p>

        {/* Brand tagline — sits directly under the wordmark, clean upright
            serif, slightly tracked. A quiet brand statement. */}
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '11px',
            letterSpacing: '0.18em',
            opacity: isMidnight ? 0.78 : 0.72,
            marginTop: '14px',
          }}
        >
          Preserving what matters most
        </p>

        {/* Main centre text — calm serif, two lines, premium feel. Replaces
            the previous "Spoken Echoes™" title block; same typography family
            (Cormorant Garamond italic) so it sits naturally on the cover.
            Font sized so each line stays on one row. */}
        <h2
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontStyle: 'italic',
            fontSize: 'clamp(13.5px, 1.95vw, 17px)',
            lineHeight: 1.55,
            letterSpacing: '0.015em',
            marginTop: '60px',
            opacity: 0.94,
          }}
        >
          <span style={{ display: 'block', whiteSpace: 'nowrap' }}>
            Written with moments you kept for them,
          </span>
          <span style={{ display: 'block', whiteSpace: 'nowrap' }}>
            to be discovered in their own time
          </span>
        </h2>

        {/* Subtle signature — smaller and softer than the main centre text,
            sits like a quiet signature line rather than a heading. */}
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 400,
            fontSize: '10.5px',
            letterSpacing: '0.20em',
            textTransform: 'uppercase',
            opacity: isMidnight ? 0.55 : 0.50,
            marginTop: '22px',
          }}
        >
          Powered by Spoken Echoes™
        </p>

        {/* Collection mark — pushed to the bottom of the cover. Small,
            tracked, foil-tinted; anchors the cover to the Memory Vault
            collection without competing with the title. */}
        <p
          style={{
            marginTop: 'auto',
            paddingTop: '36px',
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 400,
            fontSize: '9.5px',
            letterSpacing: '0.42em',
            textTransform: 'uppercase',
            backgroundImage: FOIL_GOLD_GRADIENT_SOFT,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
            opacity: 0.85,
          }}
        >
          Memory Vault Collection
        </p>
      </div>

      {/* Soft satin sheen overlay — extremely low opacity diagonal highlight,
          gives a barely-there glow to the upper-left quadrant. No gloss. */}
      <span
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius: 'inherit',
          background:
            'radial-gradient(ellipse 80% 60% at 30% 12%, rgba(255,253,245,0.04), transparent 70%)',
          mixBlendMode: 'soft-light',
        }}
      />
    </button>
  );
};

/* ── A bound page inside the open book ─────────────────────────── */
const BookPage = ({
  side,        // 'left' | 'right' | 'single'
  children,
  pageNumber,
  onCornerClick,
  cornerEnabled,
  cornerLabel,
  testid,
}) => {
  const isLeft = side === 'left';
  const isRight = side === 'right';
  const isSingle = side === 'single';

  return (
    <div
      data-testid={testid}
      className="relative"
      style={{
        flex: '1 1 0',
        // Warm cream paper — clearly warmer than white, still readable.
        // Matches the Sent2ment cream palette so pages feel like the
        // inside of a real journal, not a white screen.
        background: '#F8F1DC',
        backgroundImage: `${PAPER_TEXTURE}`,
        backgroundBlendMode: 'multiply',
        // pages are slightly warmer than pure white, with a subtle vignette
        // tucked toward the spine to suggest depth into the binding
        boxShadow: isLeft
          ? 'inset -16px 0 24px -18px rgba(11,18,32,0.35), inset 0 1px 0 rgba(255,255,255,0.7)'
          : isRight
          ? 'inset 16px 0 24px -18px rgba(11,18,32,0.35), inset 0 1px 0 rgba(255,255,255,0.7)'
          : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 2px rgba(11,18,32,0.04)',
        borderRadius: isLeft
          ? '6px 0 0 6px'
          : isRight
          ? '0 6px 6px 0'
          : '6px',
        // page-edge stack peeking from the outer side — gives bound depth
        backgroundClip: 'padding-box',
        minHeight: '560px',
        overflow: 'hidden',
      }}
    >
      {/* Outer-edge stacked page lines */}
      {!isLeft && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '8px', bottom: '8px', right: '0',
            width: '4px',
            background:
              'linear-gradient(to right, transparent, rgba(198,169,107,0.18) 30%, rgba(198,169,107,0.30) 60%, rgba(120,95,40,0.18))',
            borderRadius: '0 6px 6px 0',
          }}
        />
      )}
      {!isRight && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '8px', bottom: '8px', left: '0',
            width: '4px',
            background:
              'linear-gradient(to left, transparent, rgba(198,169,107,0.18) 30%, rgba(198,169,107,0.30) 60%, rgba(120,95,40,0.18))',
            borderRadius: '6px 0 0 6px',
          }}
        />
      )}

      {/* Page content */}
      <div className="relative h-full p-6 sm:p-9 lg:p-10 flex flex-col">
        <div className="flex-1">{children}</div>

        {/* Footer: page number on the spine side, corner-turn affordance on the outer side */}
        <div className="mt-6 flex items-end justify-between gap-3">
          {isLeft ? (
            // Left page: corner button on outer (left), page number on spine (right)
            <>
              {onCornerClick && cornerEnabled ? (
                <CornerTurnButton
                  isLeft
                  pageNumber={pageNumber}
                  cornerLabel={cornerLabel}
                  onClick={onCornerClick}
                />
              ) : (
                <span />
              )}
              <p
                className="text-[10px] tracking-[0.32em] uppercase"
                style={{ color: 'rgba(120,95,40,0.7)', fontFamily: "'Cormorant Garamond', serif" }}
              >
                {pageNumber}
              </p>
            </>
          ) : (
            // Right or single page: page number on spine (left), corner button on outer (right)
            <>
              <p
                className="text-[10px] tracking-[0.32em] uppercase"
                style={{ color: 'rgba(120,95,40,0.7)', fontFamily: "'Cormorant Garamond', serif" }}
              >
                {pageNumber}
              </p>
              {onCornerClick && cornerEnabled ? (
                <CornerTurnButton
                  pageNumber={pageNumber}
                  cornerLabel={cornerLabel}
                  onClick={onCornerClick}
                />
              ) : (
                <span />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Corner turn button — folded paper triangle that points toward the
   corner of the page it sits in. */
const CornerTurnButton = ({ isLeft = false, pageNumber, cornerLabel, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`book-page-corner-${pageNumber}`}
    aria-label={cornerLabel}
    className="group relative flex items-center gap-1.5 transition-all duration-300"
    style={{
      color: C.gold,
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '11px',
      letterSpacing: '0.32em',
      textTransform: 'uppercase',
      padding: '6px 10px',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      flexDirection: isLeft ? 'row-reverse' : 'row',
    }}
  >
    <span style={{ opacity: 0.85 }}>{cornerLabel}</span>
    <span
      aria-hidden="true"
      className={`transition-transform duration-300 ${
        isLeft ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1'
      }`}
      style={{
        display: 'inline-block',
        width: 0, height: 0,
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        ...(isLeft
          ? { borderRight: `8px solid ${C.gold}` }
          : { borderLeft: `8px solid ${C.gold}` }),
        opacity: 0.8,
      }}
    />
  </button>
);

/* ── Turning page overlay ─────────────────────────────────────────
   A double-sided page that rotates around the spine to simulate a real
   book page turn. Front face shows the page being turned (the page that
   was visible before the turn). Back face shows the page that will be
   revealed once the turn lands. Underneath, the BookShell swaps the
   underlying spread to the new state at the half-way invisible-edge
   moment so the back face lands flush onto matching content. */
const TurningPage = ({ turning, isMobile, durationS, onComplete }) => {
  const { direction, mode, frontPageNum, frontContent, backPageNum, backContent } = turning;
  const isForward = direction === 'forward';

  const padTop = isMobile ? '14px' : '18px';
  const padBottom = isMobile ? '22px' : '30px';
  const padSide = isMobile ? '14px' : '22px';

  // Position: covers the page slot that's being turned.
  let pos;
  let transformOrigin;
  if (mode === 'mobile') {
    pos = { top: padTop, bottom: padBottom, left: padSide, right: padSide };
    transformOrigin = isForward ? 'left center' : 'right center';
  } else if (isForward) {
    // Right page flips left
    pos = { top: padTop, bottom: padBottom, left: '50%', right: padSide };
    transformOrigin = 'left center';
  } else {
    // Left page flips right
    pos = { top: padTop, bottom: padBottom, left: padSide, right: '50%' };
    transformOrigin = 'right center';
  }

  const targetRotation = isForward ? -180 : 180;

  // Front-face geometry mirrors the BookPage on the side it's covering.
  const frontSide = mode === 'mobile' ? 'single' : (isForward ? 'right' : 'left');
  const backSide = mode === 'mobile' ? 'single' : (isForward ? 'left' : 'right');

  return (
    <motion.div
      style={{
        position: 'absolute',
        ...pos,
        transformStyle: 'preserve-3d',
        transformOrigin,
        zIndex: 30,
        pointerEvents: 'none',
        // soft drop shadow — peaks during mid-turn, simulating the lift of the page
        willChange: 'transform',
      }}
      initial={{ rotateY: 0 }}
      animate={{ rotateY: targetRotation }}
      transition={{ duration: durationS, ease: [0.42, 0, 0.18, 1] }}
      onAnimationComplete={onComplete}
    >
      {/* Soft shadow that intensifies mid-turn — sits on the same 3D plane as
          the page so it tilts with it. */}
      <motion.span
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.35, 0] }}
        transition={{ duration: durationS, ease: 'easeInOut', times: [0, 0.5, 1] }}
        style={{
          position: 'absolute',
          inset: '-6px',
          borderRadius: '8px',
          boxShadow: '0 18px 40px -10px rgba(11,18,32,0.45)',
          pointerEvents: 'none',
        }}
      />

      {/* Front face — the page being turned (its current side) */}
      <div
        style={{
          position: 'absolute', inset: 0,
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          display: 'flex',
        }}
      >
        <BookPage
          side={frontSide}
          pageNumber={frontPageNum}
          onCornerClick={null}
          cornerEnabled={false}
          cornerLabel=""
          testid={`turning-page-front-${frontPageNum}`}
        >
          {frontContent}
        </BookPage>
      </div>

      {/* Back face — the page that will be revealed after the turn */}
      <div
        style={{
          position: 'absolute', inset: 0,
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
          display: 'flex',
        }}
      >
        <BookPage
          side={backSide}
          pageNumber={backPageNum}
          onCornerClick={null}
          cornerEnabled={false}
          cornerLabel=""
          testid={`turning-page-back-${backPageNum}`}
        >
          {backContent}
        </BookPage>
      </div>
    </motion.div>
  );
};


/* ── BookShell ─────────────────────────────────────────────────── */
/**
 * A realistic book-style viewer that displays a sequence of pages.
 * Closed → user taps cover → opens to a 2-up spread (or 1-up on mobile).
 * Page-turn via the bottom outer corners.
 *
 * Props:
 *   totalPages: number
 *   renderPage: (pageNumber: number) => ReactNode  (1-indexed, content only)
 *   header: { overline, title, body, footnote }
 *   sectionTestId, brandTestId, coverTestId
 */
const BookShell = ({
  totalPages,
  renderPage,
  header,
  sectionTestId,
  brandTestId,
  coverTestId = 'book-cover',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coverColor, setCoverColor] = useState('midnight');
  const [pageIdx, setPageIdx] = useState(0); // 0-indexed; left page on desktop
  const [turning, setTurning] = useState(null);  // { direction, mode, frontPageNum, frontContent, backPageNum, backContent }
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const turnSwapTimeoutRef = useRef(null);

  // Page-turn animation duration (seconds). Calm and slightly weighted.
  const TURN_DURATION_S = 0.95;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Clean up any pending swap timeout on unmount.
  useEffect(() => () => {
    if (turnSwapTimeoutRef.current) clearTimeout(turnSwapTimeoutRef.current);
  }, []);

  const step = isMobile ? 1 : 2;
  const maxIdx = Math.max(0, totalPages - step);

  const goForward = useCallback(() => {
    if (turning) return;
    if (pageIdx >= maxIdx) return;
    const newIdx = Math.min(pageIdx + step, maxIdx);
    if (isMobile) {
      setTurning({
        direction: 'forward', mode: 'mobile',
        frontPageNum: pageIdx + 1,
        frontContent: renderPage(pageIdx + 1),
        backPageNum: newIdx + 1,
        backContent: renderPage(newIdx + 1),
      });
    } else {
      setTurning({
        direction: 'forward', mode: 'desktop',
        frontPageNum: pageIdx + 2,
        frontContent: renderPage(pageIdx + 2),
        backPageNum: newIdx + 1,
        backContent: renderPage(newIdx + 1),
      });
    }
    // Swap underlying to the new spread at the half-way invisible-edge moment
    // so the second half of the flip lands flush onto matching content.
    if (turnSwapTimeoutRef.current) clearTimeout(turnSwapTimeoutRef.current);
    turnSwapTimeoutRef.current = setTimeout(() => {
      setPageIdx(newIdx);
    }, (TURN_DURATION_S * 1000) / 2);
  }, [pageIdx, maxIdx, step, isMobile, turning, renderPage]);

  const goBackward = useCallback(() => {
    if (turning) return;
    if (pageIdx <= 0) return;
    const newIdx = Math.max(pageIdx - step, 0);
    if (isMobile) {
      setTurning({
        direction: 'backward', mode: 'mobile',
        frontPageNum: pageIdx + 1,
        frontContent: renderPage(pageIdx + 1),
        backPageNum: newIdx + 1,
        backContent: renderPage(newIdx + 1),
      });
    } else {
      setTurning({
        direction: 'backward', mode: 'desktop',
        frontPageNum: pageIdx + 1,
        frontContent: renderPage(pageIdx + 1),
        backPageNum: newIdx + 2,
        backContent: renderPage(newIdx + 2),
      });
    }
    if (turnSwapTimeoutRef.current) clearTimeout(turnSwapTimeoutRef.current);
    turnSwapTimeoutRef.current = setTimeout(() => {
      setPageIdx(newIdx);
    }, (TURN_DURATION_S * 1000) / 2);
  }, [pageIdx, step, isMobile, turning, renderPage]);

  // Snap pageIdx to even number on desktop so left page is always odd-indexed (1, 3, 5...)
  useEffect(() => {
    if (!isMobile && pageIdx % 2 !== 0) {
      setPageIdx((i) => Math.max(0, i - 1));
    }
  }, [isMobile, pageIdx]);

  // Keyboard nav (left/right arrows) when book is open
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') goForward();
      else if (e.key === 'ArrowLeft') goBackward();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, goForward, goBackward]);

  const leftPageNum = pageIdx + 1;
  const rightPageNum = pageIdx + 2;
  const hasNext = pageIdx + step < totalPages && !turning;
  const hasPrev = pageIdx > 0 && !turning;

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="mb-16 p-6 sm:p-10"
      style={{
        background: C.cream,
        border: `1px solid ${C.border}`,
        borderRadius: '12px',
        position: 'relative',
      }}
      data-testid={sectionTestId}
    >
      {/* Header */}
      <div className="mb-8 sm:mb-10 text-center">
        <div className="flex items-center justify-center gap-3 mb-3">
          <BookOpenIcon size={18} strokeWidth={1.4} style={{ color: C.gold }} />
          <p
            className="text-[10px] tracking-[0.35em] uppercase font-semibold"
            style={{ color: C.gold }}
            data-testid={`${sectionTestId}-overline`}
          >
            {header.overline}
          </p>
        </div>
        <h2
          className="text-2xl sm:text-3xl font-light leading-tight mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}
          data-testid={`${sectionTestId}-title`}
        >
          {header.title}
        </h2>
        <p
          className="text-sm leading-relaxed max-w-2xl mx-auto"
          style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}
          data-testid={`${sectionTestId}-body`}
        >
          {header.body}
        </p>
        {header.footnote && (
          <p
            className="text-[13px] sm:text-sm leading-relaxed mt-6 mb-2 max-w-2xl mx-auto"
            style={{
              color: 'rgba(11, 31, 58, 0.78)',
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              letterSpacing: '0.01em',
            }}
            data-testid={`${sectionTestId}-footnote`}
          >
            {header.footnote}
          </p>
        )}
      </div>

      {/* Closed cover view */}
      {!isOpen && (
        <div className="flex flex-col items-center py-6 sm:py-10" data-testid={`${sectionTestId}-closed`}>
          <CoverFace
            color={coverColor}
            onClick={() => setIsOpen(true)}
            label={coverTestId}
          />

          {/* Color toggle */}
          <div
            className="mt-8 flex items-center gap-3"
            data-testid={`${sectionTestId}-cover-color-toggle`}
          >
            <span
              className="text-[10px] tracking-[0.32em] uppercase"
              style={{ color: `${C.textSec}AA`, fontFamily: "'Cormorant Garamond', serif" }}
            >
              Cover
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setCoverColor('midnight'); }}
              data-testid="cover-swatch-midnight"
              aria-label="Midnight Navy cover"
              aria-pressed={coverColor === 'midnight'}
              className="relative rounded-full transition-all duration-300"
              style={{
                width: '20px', height: '20px',
                background: C.midnight,
                border: coverColor === 'midnight'
                  ? `2px solid ${C.gold}`
                  : '2px solid rgba(120,95,40,0.2)',
                boxShadow: '0 2px 4px rgba(11,18,32,0.3)',
                cursor: 'pointer',
              }}
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setCoverColor('cream'); }}
              data-testid="cover-swatch-cream"
              aria-label="Cream cover"
              aria-pressed={coverColor === 'cream'}
              className="relative rounded-full transition-all duration-300"
              style={{
                width: '20px', height: '20px',
                background: C.cream,
                border: coverColor === 'cream'
                  ? `2px solid ${C.gold}`
                  : '2px solid rgba(120,95,40,0.2)',
                boxShadow: '0 2px 4px rgba(11,18,32,0.15)',
                cursor: 'pointer',
              }}
            />
          </div>

          <button
            type="button"
            onClick={() => setIsOpen(true)}
            data-testid={`${sectionTestId}-open-button`}
            className="mt-7 inline-flex items-center gap-2 px-7 py-3 transition-all duration-300 hover:opacity-95"
            style={{
              background: 'transparent',
              color: C.midnight,
              border: `1px solid ${C.gold}`,
              borderRadius: '999px',
              fontFamily: "'Cormorant Garamond', serif",
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            Open the journal
          </button>
          <p
            className="mt-3 text-[11px] italic"
            style={{ color: `${C.textSec}AA`, fontFamily: 'Georgia, serif' }}
          >
            tap the cover to begin
          </p>
        </div>
      )}

      {/* Open spread view */}
      {isOpen && (
        <div className="flex flex-col items-center" data-testid={`${sectionTestId}-open`}>
          {/* Subtle "back" link — top-left of the journal view, returns to the
              closed cover state. Styled as a soft italic link so it reads as
              an exit, not a UI button. */}
          <div className="w-full max-w-5xl mb-3 px-1">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              data-testid="book-close"
              aria-label="Back to the journal cover"
              className="inline-flex items-center gap-1.5 transition-opacity hover:opacity-90"
              style={{
                color: `${C.textSec}CC`,
                background: 'transparent',
                border: 'none',
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic',
                fontSize: '13px',
                letterSpacing: '0.02em',
                cursor: 'pointer',
                padding: '4px 2px',
              }}
            >
              <ChevronLeft size={14} strokeWidth={1.4} /> Back
            </button>
          </div>

          {/* Top toolbar: previous / spread indicator / next */}
          <div className="w-full max-w-5xl flex items-center justify-between mb-4 px-1">
            <button
              type="button"
              onClick={goBackward}
              disabled={!hasPrev}
              data-testid="book-prev"
              aria-label="Previous page"
              className="inline-flex items-center gap-1.5 transition-opacity"
              style={{
                color: C.midnight,
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '11px',
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                background: 'transparent',
                border: 'none',
                opacity: hasPrev ? 0.85 : 0.25,
                cursor: hasPrev ? 'pointer' : 'not-allowed',
              }}
            >
              <ChevronLeft size={14} strokeWidth={1.6} /> Previous
            </button>
            <p
              className="text-[10px] tracking-[0.32em] uppercase"
              style={{ color: `${C.textSec}AA`, fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="book-page-indicator"
            >
              {isMobile
                ? `Page ${leftPageNum} of ${totalPages}`
                : `Pages ${leftPageNum}–${Math.min(rightPageNum, totalPages)} of ${totalPages}`}
            </p>
            <button
              type="button"
              onClick={goForward}
              disabled={!hasNext}
              data-testid="book-next"
              aria-label="Next page"
              className="inline-flex items-center gap-1.5 transition-opacity"
              style={{
                color: C.midnight,
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '11px',
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                background: 'transparent',
                border: 'none',
                opacity: hasNext ? 0.85 : 0.25,
                cursor: hasNext ? 'pointer' : 'not-allowed',
              }}
            >
              Next <ChevronRight size={14} strokeWidth={1.6} />
            </button>
          </div>

          {/* Book base — provides hardback depth shadow and the "open book" silhouette */}
          <div
            className="relative w-full max-w-5xl"
            style={{
              perspective: '1800px',
              perspectiveOrigin: 'center 30%',
            }}
          >
            <div
              className="relative mx-auto"
              style={{
                width: '100%',
                background:
                  'linear-gradient(to bottom, rgba(11,18,32,0.04), rgba(11,18,32,0.10))',
                borderRadius: '8px',
                padding: isMobile ? '14px 14px 22px' : '18px 22px 30px',
                boxShadow:
                  '0 36px 60px -28px rgba(11,18,32,0.45), ' +
                  '0 18px 30px -18px rgba(11,18,32,0.30)',
              }}
            >
              {/* Center spine — only on desktop spread */}
              {!isMobile && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: '18px', bottom: '30px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '24px',
                    background:
                      'linear-gradient(to right, ' +
                      'transparent 0%, rgba(11,18,32,0.18) 35%, rgba(11,18,32,0.32) 50%, rgba(11,18,32,0.18) 65%, transparent 100%)',
                    pointerEvents: 'none',
                    zIndex: 2,
                  }}
                />
              )}

              <AnimatePresence>
                {turning && (
                  <TurningPage
                    key="turning-page"
                    turning={turning}
                    isMobile={isMobile}
                    durationS={TURN_DURATION_S}
                    onComplete={() => setTurning(null)}
                  />
                )}
              </AnimatePresence>

              <div
                className="flex"
                style={{
                  gap: 0,
                  transformStyle: 'preserve-3d',
                }}
                data-testid={`book-spread-${pageIdx}`}
              >
                {isMobile ? (
                  <BookPage
                    side="single"
                    pageNumber={leftPageNum}
                    onCornerClick={hasNext ? goForward : null}
                    cornerEnabled={hasNext}
                    cornerLabel="Turn"
                    testid={`book-spread-${pageIdx}-page`}
                  >
                    {renderPage(leftPageNum)}
                  </BookPage>
                ) : (
                  <>
                    <BookPage
                      side="left"
                      pageNumber={leftPageNum}
                      onCornerClick={hasPrev ? goBackward : null}
                      cornerEnabled={hasPrev}
                      cornerLabel="Back"
                      testid={`book-spread-${pageIdx}-left`}
                    >
                      {renderPage(leftPageNum)}
                    </BookPage>
                    <BookPage
                      side="right"
                      pageNumber={rightPageNum <= totalPages ? rightPageNum : ''}
                      onCornerClick={hasNext ? goForward : null}
                      cornerEnabled={hasNext}
                      cornerLabel="Turn"
                      testid={`book-spread-${pageIdx}-right`}
                    >
                      {rightPageNum <= totalPages ? (
                        renderPage(rightPageNum)
                      ) : (
                        // Final blank flyleaf if odd number of pages — preserved for safety
                        <div
                          className="h-full flex items-center justify-center"
                          style={{ color: `${C.textSec}88`, fontFamily: 'Georgia, serif' }}
                        >
                          <p className="italic text-sm">— end of journal —</p>
                        </div>
                      )}
                    </BookPage>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Bottom helper */}
          <p
            className="mt-5 text-[11px] italic text-center"
            style={{ color: `${C.textSec}99`, fontFamily: 'Georgia, serif' }}
          >
            Tap the bottom outer corner to turn the page · or use ← →
          </p>
        </div>
      )}

      {/* Brand mark */}
      <div className="mt-10 flex flex-col items-center gap-3" data-testid={brandTestId}>
        <div
          aria-hidden="true"
          style={{
            width: '64px', height: '1px',
            background: `linear-gradient(to right, transparent, ${C.gold}, transparent)`,
          }}
        />
        <p
          className="text-[10px] tracking-[0.4em] uppercase"
          style={{ color: `${C.gold}BB`, fontFamily: "'Cormorant Garamond', serif" }}
        >
          Sent2ment
        </p>
      </div>
    </motion.section>
  );
};

export default BookShell;
